import type {
  NativeGlassRefreshDetail,
  NativeGlassStatus,
} from "./native-glass-types";

export type NativeGlassAppearance = "light" | "dark";
type NativeGlassDiagnosticMode = "normal" | "off" | "no-frames";

export const NATIVE_GLASS_REFRESH_EVENT = "denker:native-glass-refresh";
export const NATIVE_GLASS_DIAGNOSTIC_MODE_KEY =
  "denker.nativeGlass.diagnosticMode";
/** Solid fraction stamped on `<html>` by `lib/transparency.ts`: 0 glass, 1 solid. */
export const MATERIAL_ALPHA_PROPERTY = "--denker-material-alpha";

let nativeGlassDiagnosticFileMode: NativeGlassDiagnosticMode | null = null;
let nativeGlassUnsupported = false;

/** Rust answers `unsupported` only on platforms without native glass
 * (Windows), which never changes within a process: the first such reply ends
 * native glass work for the session and CSS renders every surface. */
export const markNativeGlassUnsupported = () => {
  nativeGlassUnsupported = true;
};

export const isNativeGlassUnsupported = () => nativeGlassUnsupported;

const parseNativeGlassDiagnosticMode = (
  value: string | null | undefined,
): NativeGlassDiagnosticMode | null =>
  value === "off" || value === "no-frames" || value === "normal" ? value : null;

export const readNativeAppearance = (): NativeGlassAppearance =>
  document.documentElement.classList.contains("light") ? "light" : "dark";

export const readMaterialAlpha = (): number => {
  const alpha = Number.parseFloat(
    document.documentElement.style.getPropertyValue(MATERIAL_ALPHA_PROPERTY),
  );
  return Number.isFinite(alpha) ? Math.min(1, Math.max(0, alpha)) : 0;
};

export const stampNativeGlass = (
  status: NativeGlassStatus | null,
  appearance: NativeGlassAppearance | "" = "",
  morphStartReceiptsReady = true,
) => {
  const dataset = document.documentElement.dataset;
  // CSS keys heavy rules on these attributes, so only write on change.
  const setIfChanged = (key: string, value: string | undefined) => {
    if (value === undefined) {
      if (key in dataset) delete dataset[key];
    } else if (dataset[key] !== value) {
      dataset[key] = value;
    }
  };
  const active = status !== null && status.mode !== "unsupported";
  setIfChanged("desktopNativeGlass", active ? "true" : "false");
  setIfChanged("desktopNativeGlassCount", String(status?.surfaceCount ?? 0));
  setIfChanged("desktopNativeGlassMode", status ? status.mode : undefined);
  setIfChanged("desktopNativeGlassMorphs", status?.morphs ? "true" : undefined);
  setIfChanged(
    "desktopNativeGlassMorphStartReceipts",
    status?.morphStartReceipts && morphStartReceiptsReady ? "true" : undefined,
  );
  setIfChanged("desktopNativeGlassAppearance", appearance || undefined);
};

/** Plate morphs need native support, and the plate must be the surface's only
 * material: the visual-effect backend paints a full-size veil in the webview
 * that a growing plate would sit inside. */
export const nativeGlassMorphsEnabled = (): boolean => {
  const dataset = document.documentElement.dataset;
  return (
    dataset.desktopNativeGlassMorphs === "true" &&
    dataset.desktopNativeGlassMode === "liquid-glass"
  );
};

export function requestNativeGlassRefresh(detail?: NativeGlassRefreshDetail) {
  if (nativeGlassUnsupported) return;
  const event = detail
    ? new CustomEvent(NATIVE_GLASS_REFRESH_EVENT, { detail })
    : new Event(NATIVE_GLASS_REFRESH_EVENT);
  window.dispatchEvent(event);
}

export const readNativeGlassDiagnosticMode = (): NativeGlassDiagnosticMode => {
  if (nativeGlassDiagnosticFileMode) return nativeGlassDiagnosticFileMode;
  if (typeof window === "undefined") return "normal";
  try {
    return (
      parseNativeGlassDiagnosticMode(
        window.localStorage.getItem(NATIVE_GLASS_DIAGNOSTIC_MODE_KEY),
      ) ?? "normal"
    );
  } catch {
    return "normal";
  }
};

export const startNativeGlassDiagnosticModePolling = () => {
  if (
    !import.meta.env.DEV ||
    typeof window === "undefined" ||
    typeof fetch !== "function"
  ) {
    return () => {};
  }

  let disposed = false;
  const poll = () => {
    void fetch(`/native-glass-diagnostic-mode.txt?t=${Date.now()}`, {
      cache: "no-store",
    })
      .then((response) => (response.ok ? response.text() : ""))
      .then((text) => {
        if (disposed) return;
        const next = parseNativeGlassDiagnosticMode(text.trim()) ?? null;
        if (next === nativeGlassDiagnosticFileMode) return;
        nativeGlassDiagnosticFileMode = next;
        requestNativeGlassRefresh();
      })
      .catch(() => {});
  };
  poll();
  const timer = window.setInterval(poll, 500);
  return () => {
    disposed = true;
    window.clearInterval(timer);
  };
};
